import React, { useState, useEffect } from 'react';
import Button from '../shared/Button';
import Modal from '../shared/Modal';
import ConfirmDialog from '../shared/ConfirmDialog';
import { useToast } from '../../hooks/useToast';
import {
    getAllDocks,
    createDock,
    updateDock,
    deleteDock,
    getDocksByDoorGroup
} from '../../services/firestore/dockService';
import { getAllDoorGroups } from '../../services/firestore/doorGroupService';
import { getDockTypes } from '../../services/firestore/systemConfigService';

const DockConfig = ({ user }) => {
    const { success, error: toastError } = useToast();
    const [docks, setDocks] = useState([]);
    const [doorGroups, setDoorGroups] = useState([]);
    const [dockTypes, setDockTypes] = useState([]);
    const [loading, setLoading] = useState(false);

    // Filter state
    const [selectedGroup, setSelectedGroup] = useState('all');

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({});

    const [confirmConfig, setConfirmConfig] = useState({
        isOpen: false,
        title: '',
        message: '',
        type: 'warning',
        confirmText: 'Onayla',
        cancelText: 'Vazgeç',
        onConfirm: () => { }
    });

    const showConfirm = (config) => {
        setConfirmConfig({
            isOpen: true,
            title: config.title || 'Onay Gerekiyor',
            message: config.message || 'Bu işlemi yapmak istediğinize emin misiniz?',
            type: config.type || 'warning',
            confirmText: config.confirmText || 'Evet, Onayla',
            cancelText: config.cancelText || 'İptal',
            onConfirm: () => {
                config.onConfirm();
                setConfirmConfig(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    useEffect(() => {
        if (user?.tenantId) {
            loadData();
        }
    }, [user?.tenantId]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [docksData, doorGroupsData, dockTypesData] = await Promise.all([
                getAllDocks(user.tenantId),
                getAllDoorGroups(user.tenantId, true),
                getDockTypes(user.tenantId, true)
            ]);
            setDocks(docksData);
            setDoorGroups(doorGroupsData);

            // If no dock types exist, maybe we should warn or just show empty? 
            // Or rely on the hardcoded fallback if array is empty?
            // Better to use dynamic, if empty show 'General' as fallback option in UI
            setDockTypes(dockTypesData);

        } catch (err) {
            console.error(err);
            toastError('Veriler yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const filteredDocks = selectedGroup === 'all'
        ? docks
        : docks.filter(d => d.doorGroupId === selectedGroup);

    const handleOpenModal = (item = null) => {
        if (item) {
            setEditingItem(item);
            setFormData({
                ...item
            });
        } else {
            setEditingItem(null);
            setFormData({
                tenantId: user.tenantId,
                name: '',
                type: 'general',
                doorGroupId: selectedGroup !== 'all' ? selectedGroup : '',
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingItem(null);
        setFormData({});
    };

    // Helper to get group name
    const getGroupName = (groupId) => {
        const g = doorGroups.find(dg => dg.id === groupId);
        return g ? g.name : '-';
    };

    const handleSave = async () => {
        try {
            // Validate
            if (!formData.name || !formData.doorGroupId) {
                toastError('Lütfen gerekli alanları doldurun (İsim, Rampa Grubu).');
                return;
            }

            const payload = {
                ...formData,
                tenantId: user.tenantId // Ensure tenantId is always set
            };

            if (editingItem) {
                await updateDock(editingItem.id, payload, user.tenantId);
                success('Rampa güncellendi');
            } else {
                await createDock(payload, user.tenantId);
                success('Rampa oluşturuldu');
            }
            handleCloseModal();
            loadData();
        } catch (err) {
            toastError('Kaydetme hatası: ' + err.message);
        }
    };

    const handleDelete = async (id) => {
        showConfirm({
            title: 'Rampayı Sil',
            message: 'Bu rampayı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.',
            type: 'error',
            confirmText: 'Evet, Sil',
            onConfirm: async () => {
                try {
                    await deleteDock(id, user.tenantId);
                    success('Rampa silindi');
                    loadData();
                } catch (err) {
                    toastError('Silinemedi: ' + err.message);
                }
            }
        });
    };



    // Helper to get dock type name
    const getDockTypeName = (code) => {
        const t = dockTypes.find(dt => dt.code === code);
        return t ? t.name : code;
    };

    return (
        <div>
            {/* Toolbar */}
            <div className="flex justify-between items-center mb-md">
                <div className="flex items-center gap-md">
                    <select
                        className="select"
                        value={selectedGroup}
                        onChange={(e) => setSelectedGroup(e.target.value)}
                        style={{ minWidth: '200px' }}
                    >
                        <option value="all">Tüm Rampa Grupları</option>
                        {doorGroups.map(dg => (
                            <option key={dg.id} value={dg.id}>{dg.name}</option>
                        ))}
                    </select>
                </div>
                <Button variant="primary" onClick={() => handleOpenModal()}>
                    + Yeni Rampa
                </Button>
            </div>

            {/* Table */}
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <table className="table">
                    <thead>
                        <tr>
                            <th>Rampa Adı</th>
                            <th>Rampa Grubu</th>
                            <th>Tip</th>
                            <th>Durum</th>
                            <th>İşlemler</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="6" className="text-center p-lg">Yükleniyor...</td></tr>
                        ) : filteredDocks.length === 0 ? (
                            <tr><td colSpan="6" className="text-center p-lg text-secondary">Kayıt bulunamadı.</td></tr>
                        ) : (
                            filteredDocks.map(dock => (
                                <tr key={dock.id}>
                                    <td style={{ fontWeight: 600 }}>{dock.name}</td>
                                    <td>{getGroupName(dock.doorGroupId)}</td>
                                    <td>
                                        <span className="badge badge-neutral" style={{ textTransform: 'uppercase' }}>
                                            {getDockTypeName(dock.type)}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`badge ${dock.isActive !== false ? 'badge-success' : 'badge-neutral'}`}>
                                            {dock.isActive !== false ? 'Aktif' : 'Pasif'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="flex gap-sm">
                                            <Button size="sm" variant="secondary" onClick={() => handleOpenModal(dock)}>Düzenle</Button>
                                            <Button size="sm" variant="danger" onClick={() => handleDelete(dock.id)}>Sil</Button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title={editingItem ? 'Rampa Düzenle' : 'Yeni Rampa Ekle'}
            >
                <div className="form-group">
                    <label className="form-label">Rampa Adı *</label>
                    <input
                        type="text"
                        className="input"
                        value={formData.name || ''}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Örn: Rampa 5"
                    />
                </div>

                <div className="form-group">
                    <label className="form-label">Rampa Grubu *</label>
                    <select
                        className="select"
                        value={formData.doorGroupId || ''}
                        onChange={(e) => setFormData({ ...formData, doorGroupId: e.target.value })}
                    >
                        <option value="">Seçiniz</option>
                        {doorGroups.map(dg => (
                            <option key={dg.id} value={dg.id}>{dg.name}</option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label className="form-label">Rampa Tipi</label>
                    <select
                        className="select"
                        value={formData.type || 'general'}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    >
                        {dockTypes.length > 0 ? (
                            dockTypes.map(dt => (
                                <option key={dt.id} value={dt.code}>{dt.name}</option>
                            ))
                        ) : (
                            <>
                                <option value="general">Genel</option>
                                <option value="cold">Soğuk Hava</option>
                                <option value="dry">Kuru Yük</option>
                                <option value="express">Express</option>
                            </>
                        )}
                    </select>
                </div>



                <div className="flex justify-end gap-md mt-lg">
                    <Button variant="secondary" onClick={handleCloseModal}>İptal</Button>
                    <Button variant="primary" onClick={handleSave}>Kaydet</Button>
                </div>
            </Modal>

            <ConfirmDialog
                isOpen={confirmConfig.isOpen}
                onClose={() => setConfirmConfig(prev => ({ ...prev, isOpen: false }))}
                onConfirm={confirmConfig.onConfirm}
                title={confirmConfig.title}
                message={confirmConfig.message}
                type={confirmConfig.type}
                confirmText={confirmConfig.confirmText}
                cancelText={confirmConfig.cancelText}
            />
        </div>
    );
};

export default DockConfig;
